import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Col,
  Table,
  Input,
  Button,
  Select,
  Row,
  message,
  Tag,
  Tooltip,
  Badge,
  Pagination,
  Checkbox,
  Dropdown,
  Card,
  Spin,
  Menu,
} from 'antd';
import { TaskUpdateModal } from './modals';
import TaskDrawerFull from './TaskDrawerFull';
import { BackToTop } from './shared';
import { useTaskContext } from '../../../../core/components/tasks/useTaskContext';
import {
  taskTypes,
  subTypes,
  advertiserTypes,
  urgencyTypes,
  urgencyColors,
  DEFAULT_STATUSES,
  statusTypes,
} from '../configs/task.config';
import { isRestrictedTaskLevel } from '../utils/task.utils';
import { withRouter, Link } from 'react-router-dom';
import qs from 'qs';
import useSearchParams from '../../../../core/hooks/useSearchParams';
import { LeftOutlined, RightOutlined, SyncOutlined, QuestionCircleOutlined, DownOutlined, FilterOutlined, ExclamationCircleOutlined, PlusOutlined, LoadingOutlined, SearchOutlined, CloseOutlined, SettingOutlined, DownloadOutlined } from '@ant-design/icons';
import { useTaskExport } from '../hooks/useTaskExport';
import { text, link, space, activityIcon, icon } from '../configs/designTokens';
import '../styles/index.scss';
import {
  getRowClassName,
  formatDueDate,
  formatStatusLabel,
  URGENCY_CONFIG,
} from '../utils/taskTableUtils';
import { debounce } from 'lodash';
import { useOrgContext } from '../../../../core/components/orgs/useOrgContext';
import useResponsiveTask from '../hooks/useResponsiveTask';
import TaskMobileCards from './shared/TaskMobileCards';

const { Option, OptGroup } = Select;

const UnassignedTasksView = ({ history }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [projectFilter, setProjectFilter] = useState('noProject');
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const { width: windowWidth, isMobile, isDesktop } = useResponsiveTask();
  const {
    fetchTasksPaginated,
    task,
    unassignedTaskPagination,
    setUnassignedTaskPagination,
    tasksLoading,
    updateTask,
    updateTaskLoading,
    currentUser,
  } = useTaskContext();
  const { fetchBasicOrgsDetails, basicOrgsDetails, basicOrgsDetailsLoading } = useOrgContext();
  const { exportTasks, isExporting } = useTaskExport();
  const [searchParams, setSearchParams] = useSearchParams({
    requestTypes: '',
    requestSubTypes: '',
    advertiserTypes: '',
    advertiser: '',
    name: '',
    urgency: '',
    status: '',
    showAllStatuses: false,
    showProjectTasks: false,
  });

  const buildUnassignedWhere = useCallback(() => {
    const requestTypesArray = searchParams.requestTypes ? searchParams.requestTypes.split(',') : [];
    const requestSubTypesArray = searchParams.requestSubTypes ? searchParams.requestSubTypes.split(',') : [];
    const advertiserTypesArray = searchParams.advertiserTypes ? searchParams.advertiserTypes.split(',') : [];
    const advertiserArray = searchParams.advertiser ? searchParams.advertiser.split(',') : [];
    const urgencyArray = searchParams.urgency ? searchParams.urgency.split(',') : [];
    const statusArray = searchParams.status ? searchParams.status.split(',') : [];

    return {
      AND: [
        {
          name_contains: searchParams.name || '',
          assigneeUser: null,
          assigneeUsers_every: { id: null },
        },
        ...(requestTypesArray.length ? [{ requestType_in: requestTypesArray }] : []),
        ...(requestSubTypesArray.length ? [{ requestSubType_in: requestSubTypesArray }] : []),
        ...(advertiserTypesArray.length
          ? [{ createdBy: { roles_some: { org: { level_in: advertiserTypesArray } } } }]
          : []),
        ...(advertiserArray.length
          ? [{ createdBy: { roles_some: { org: { name_in: advertiserArray } } } }]
          : []),
        ...(urgencyArray.length ? [{ urgency_in: urgencyArray }] : []),
        ...(statusArray.length
          ? [{ status_in: statusArray }]
          : searchParams.showAllStatuses === true || searchParams.showAllStatuses === 'true'
            ? []
            : [{ status_in: DEFAULT_STATUSES }]),
        ...(projectFilter !== 'all'
          ? projectFilter === 'projectOnly'
            ? [{ NOT: { project: null } }]
            : [{ project: null }]
          : []),
      ],
      OR: [
        {
          createdBy: {
            roles_some: {
              org: {
                OR: [
                  { parentOrg_some: { id: currentUser.role.org.id } },
                  { id: currentUser.role.org.id },
                ],
              },
            },
          },
        },
        {
          assigneeOrg: {
            id: currentUser.role.org.id,
            OR: [
              { parentOrg_some: { id: currentUser.role.org.id } },
              { id: currentUser.role.org.id },
            ],
          },
        },
      ],
    };
  }, [searchParams, projectFilter, currentUser]);

  const handleExportUnassigned = useCallback(() => {
    exportTasks(buildUnassignedWhere());
  }, [exportTasks, buildUnassignedWhere]);

  // Memoize filtered data to prevent infinite re-renders
  const unassignedData = useMemo(
    () => task.filter((item) => item.assigneeUser === null),
    [task]
  );
  const [selectedUpdate, setSelectedUpdate] = useState([]);

  // Infinite scroll state for mobile
  const sentinelRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const lastProcessedTasksRef = useRef(null);
  const [accumulatedTasks, setAccumulatedTasks] = useState([]);

  const level =
    currentUser && currentUser.role && currentUser.role.org && currentUser.role.org.level;

  useEffect(() => {
    handleFetchTasksPaginated({
      skip: (unassignedTaskPagination.current - 1) * unassignedTaskPagination.pageSize || 0,
      first: unassignedTaskPagination.pageSize,
      ...searchParams,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unassignedTaskPagination.pageSize, unassignedTaskPagination.current, searchParams, projectFilter]);

  const debouncedFetchRef = useRef(
    debounce(
      ({
        skip,
        first,
        sortField = null,
        sortOrder = 'updatedAt_DESC',
        name = '',
        requestTypes = '',
        requestSubTypes = '',
        advertiserTypes = '',
        advertiser = '',
        urgency = '',
        status = '',
        showAllStatuses = false,
        showProjectTasks = false,
      }) => {
        const requestTypesArray = requestTypes ? requestTypes.split(',') : [];
        const requestSubTypesArray = requestSubTypes ? requestSubTypes.split(',') : [];
        const advertiserTypesArray = advertiserTypes ? advertiserTypes.split(',') : [];
        const advertiserArray = advertiser ? advertiser.split(',') : [];
        const urgencyArray = urgency ? urgency.split(',') : [];
        const statusArray = status ? status.split(',') : [];
        const orderBy =
          sortOrder === 'updatedAt_DESC'
            ? 'updatedAt_DESC'
            : sortField
              ? `${sortField}_${sortOrder}`
              : 'updatedAt_DESC';

        fetchTasksPaginated({
          variables: {
            where: {
              AND: [
                {
                  name_contains: name,
                  assigneeUser: null,
                  assigneeUsers_every: { id: null },
                },
                ...(requestTypesArray.length ? [{ requestType_in: requestTypesArray }] : []),
                ...(requestSubTypesArray.length ? [{ requestSubType_in: requestSubTypesArray }] : []),
                ...(advertiserTypesArray.length
                  ? [
                      {
                        createdBy: {
                          roles_some: { org: { level_in: advertiserTypesArray } },
                        },
                      },
                    ]
                  : []),
                ...(advertiserArray.length
                  ? [
                      {
                        createdBy: {
                          roles_some: { org: { name_in: advertiserArray } },
                        },
                      },
                    ]
                  : []),
                ...(urgencyArray.length ? [{ urgency_in: urgencyArray }] : []),
                ...(statusArray.length
                  ? [{ status_in: statusArray }]
                  : showAllStatuses === true || showAllStatuses === 'true'
                    ? []
                    : [{ status_in: DEFAULT_STATUSES }]),
                ...(projectFilter !== 'all'
                  ? projectFilter === 'projectOnly'
                    ? [{ NOT: { project: null } }]
                    : [{ project: null }]
                  : []),
              ],
              OR: [
                {
                  createdBy: {
                    roles_some: {
                      org: {
                        OR: [
                          {
                            parentOrg_some: {
                              id: currentUser.role.org.id,
                            },
                          },
                          {
                            id: currentUser.role.org.id,
                          },
                        ],
                      },
                    },
                  },
                },
                {
                  assigneeOrg: {
                    id: currentUser.role.org.id,
                    OR: [
                      { parentOrg_some: { id: currentUser.role.org.id } },
                      { id: currentUser.role.org.id },
                    ],
                  },
                },
              ],
            },
            orderBy,
            skip,
            first,
          },
        });
      },
      500
    )
  );

  const handleFetchTasksPaginated = useCallback(
    (params) => debouncedFetchRef.current(params),
    []
  );

  const fetchAdvertiserOrgs = useCallback(
    debounce((name = '') => {
      fetchBasicOrgsDetails({
        variables: {
          first: 1000,
          skip: 0,
          where: {
            AND: [
              {
                OR: [
                  {
                    assignedTasks_some: {
                      createdBy: {
                        roles_some: {
                          org: {
                            OR: [
                              { id: currentUser.role.org.id },
                              { parentOrg_some: { id: currentUser.role.org.id } },
                            ],
                          },
                        },
                      },
                    },
                  },
                  {
                    channelPartnerTasks_some: {
                      createdBy: {
                        roles_some: {
                          org: {
                            OR: [
                              { id: currentUser.role.org.id },
                              { parentOrg_some: { id: currentUser.role.org.id } },
                            ],
                          },
                        },
                      },
                    },
                  },
                ],
              },
              {
                roles_some: {
                  roleItems_some: { feature: 'TASK_MANAGEMENT' },
                },
              },
              { name_contains: name },
            ],
          },
        },
      });
    }, 500),
    [currentUser.role.org.id, fetchBasicOrgsDetails]
  );

  const handleTaskTypeFilter = useCallback((selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      requestTypes: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setUnassignedTaskPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchParams, setSearchParams, setUnassignedTaskPagination]);

  const handleTaskSubTypeFilter = useCallback((selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      requestSubTypes: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setUnassignedTaskPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchParams, setSearchParams, setUnassignedTaskPagination]);

  const handleStatusFilter = useCallback((selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      status: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setUnassignedTaskPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchParams, setSearchParams, setUnassignedTaskPagination]);

  const handleAdvertiserTypeFilter = useCallback((selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      advertiserTypes: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setUnassignedTaskPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchParams, setSearchParams, setUnassignedTaskPagination]);

  const handleAdvertiserFilter = useCallback((selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      advertiser: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setUnassignedTaskPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchParams, setSearchParams, setUnassignedTaskPagination]);

  const handleUrgencyFilter = useCallback((selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      urgency: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setUnassignedTaskPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchParams, setSearchParams, setUnassignedTaskPagination]);

  const handlePaginationChange = (params) => {
    const updatedPagination = {
      ...unassignedTaskPagination,
      current: params.page || unassignedTaskPagination.current,
      pageSize: params.pageSize || unassignedTaskPagination.pageSize,
    };

    const sortOrder =
      params.sortOrder === 'ascend'
        ? 'ASC'
        : params.sortOrder === 'descend'
          ? 'DESC'
          : 'updatedAt_DESC';

    const sortField =
      sortOrder === 'updatedAt_DESC'
        ? null
        : params.sortField || unassignedTaskPagination.sortField;

    setUnassignedTaskPagination(updatedPagination);

    const newQuery = { ...searchParams, sortField, sortOrder };
    if (sortField === null) {
      newQuery.sortField = undefined;
    }
    const queryString = qs.stringify(newQuery, { addQueryPrefix: true });
    history.push(`/tasks/unassigned/${updatedPagination.current}${queryString}`);

    handleFetchTasksPaginated({
      skip: (updatedPagination.current - 1) * updatedPagination.pageSize || 0,
      first: updatedPagination.pageSize,
      sortField,
      sortOrder,
    });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const sortField = sorter.field || null;
    const sortOrder = sorter.order || null;

    handlePaginationChange({
      page: 1,
      sortField,
      sortOrder,
    });
  };

  const handlePaginationSizeChange = (current, pageSize) => {
    handlePaginationChange({
      page: 1,
      pageSize,
    });
  };

  const itemRender = (page, type, originalElement) => {
    if (type === 'prev') {
      return (
        <Button icon={<LeftOutlined />} type="text">
          Previous
        </Button>
      );
    }
    if (type === 'next') {
      return (
        <Button type="text">
          Next
          <RightOutlined />
        </Button>
      );
    }
    return originalElement;
  };

  // Accumulate tasks for mobile infinite scroll
  useEffect(() => {
    // Skip if no data
    if (!unassignedData || unassignedData.length === 0) {
      if (unassignedTaskPagination.current === 1) {
        setAccumulatedTasks([]);
      }
      return;
    }

    // Skip if we've already processed this exact data
    if (unassignedData === lastProcessedTasksRef.current) {
      return;
    }

    lastProcessedTasksRef.current = unassignedData;

    if (!isMobile) {
      // Desktop: just use the current page data
      setAccumulatedTasks(unassignedData);
      return;
    }

    // Mobile: accumulate data for infinite scroll
    if (unassignedTaskPagination.current === 1) {
      setAccumulatedTasks([...unassignedData]);
    } else {
      setAccumulatedTasks((prev) => {
        const existingIds = new Set(prev.map((t) => t.id));
        const newTasks = unassignedData.filter((t) => !existingIds.has(t.id));
        if (newTasks.length === 0) {
          return prev;
        }
        return [...prev, ...newTasks];
      });
    }

    loadingMoreRef.current = false;
  }, [unassignedData, isMobile, unassignedTaskPagination]);

  // Display tasks based on device
  const displayTasks = isMobile ? accumulatedTasks : unassignedData;

  // Infinite scroll helpers
  const hasMorePages = accumulatedTasks.length > 0 && unassignedTaskPagination.total > accumulatedTasks.length;
  const progressPercent = unassignedTaskPagination.total > 0 ? (accumulatedTasks.length / unassignedTaskPagination.total) * 100 : 0;

  const loadMore = useCallback(() => {
    if (!tasksLoading && hasMorePages && !loadingMoreRef.current) {
      loadingMoreRef.current = true;
      handlePaginationChange({ page: unassignedTaskPagination.current + 1, pageSize: unassignedTaskPagination.pageSize });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasksLoading, hasMorePages, unassignedTaskPagination.current]);

  useEffect(() => {
    if (!isMobile || !sentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMorePages && !tasksLoading && !loadingMoreRef.current) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [isMobile, hasMorePages, tasksLoading, loadMore]);

  useEffect(() => {
    if (searchParams.advertiser === '') {
      fetchAdvertiserOrgs();
    }
  }, [fetchAdvertiserOrgs, searchParams.advertiser]);

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleUpdate = async (values) => {
    try {
      message.loading('Updating...', 0);
      const { dueDate, status, assigneeOrg, assignee } = values;

      const updatedData = {
        dueDate: dueDate ? dueDate.toISOString() : null,
        status,
        assigneeOrg: assigneeOrg ? { connect: { id: assigneeOrg } } : null,
        assigneeUsers: assignee ? { connect: [{ id: assignee }] } : { disconnectAll: true },
      };

      await updateTask({
        variables: { data: updatedData, where: { id: selectedUpdate.id } },
      });
      setIsModalVisible(false);
      message.destroy();
      message.success('Updated successfully!');
      history.push(`/tasks/main/1`);
    } catch (error) {
      message.destroy();
      message.error('Failed to update.');
      console.error('Failed to update task:', error);
    }
  };

  // Local state for instant input feedback; debounce only the URL/API update
  const [localSearchValue, setLocalSearchValue] = useState(searchParams.name || '');
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const debouncedSearchUpdate = useMemo(
    () =>
      debounce((value) => {
        setSearchParamsRef.current({ ...searchParamsRef.current, name: value });
        setUnassignedTaskPagination((prev) => ({ ...prev, current: 1 }));
      }, 300),
    [setUnassignedTaskPagination]
  );

  const handleSearchChange = useCallback(
    (value) => {
      setLocalSearchValue(value);
      debouncedSearchUpdate(value);
    },
    [debouncedSearchUpdate]
  );

  useEffect(() => () => debouncedSearchUpdate.cancel(), [debouncedSearchUpdate]);

  const handleTaskClick = useCallback((recordOrId) => {
    const id = typeof recordOrId === 'string' ? recordOrId : recordOrId.id;
    setSelectedTaskId(id);
    setDrawerVisible(true);
  }, []);

  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setSelectedTaskId(null);
  };

  const handleTaskUpdate = useCallback(() => {
    handleFetchTasksPaginated({
      skip: (unassignedTaskPagination.current - 1) * unassignedTaskPagination.pageSize || 0,
      first: unassignedTaskPagination.pageSize,
      ...searchParams,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, unassignedTaskPagination, handleFetchTasksPaginated]);

  const isTablet = windowWidth >= 768 && windowWidth < 992;

  const columns = useMemo(() => {
    const cols = [];

    // Task Name column (always visible)
    cols.push({
      title: 'Task',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      fixed: isMobile || isTablet ? false : 'left',
      width: isMobile ? 180 : isTablet ? 280 : undefined,
      className: 'task-table__cell--name',
      render: (text, record) => {
        const { isOverdue, daysLate } = formatDueDate(record.dueDate, record.status);
        const statusLabel = formatStatusLabel(record.status);
        const urgencyConfig = URGENCY_CONFIG[record.urgency] || URGENCY_CONFIG.LOW;

        // Status CSS class mapping
        const statusCssMap = {
          NOT_STARTED: 'todo',
          IN_PROGRESS: 'in-progress',
          READY_FOR_REVIEW: 'in-review',
          COMPLETED: 'done',
          CANCELED: 'cancelled',
          ON_HOLD: 'on-hold',
        };
        const statusClass = statusCssMap[record.status] || 'todo';

        // Handle click - normal click opens drawer, ctrl/cmd+click or middle-click opens in new tab
        const handleClick = (e) => {
          if (e.ctrlKey || e.metaKey || e.button === 1) {
            return; // Let the browser handle it (open in new tab)
          }
          e.preventDefault();
          handleTaskClick(record);
        };

        return (
          <a
            href={`/tasks/details/${record.id}`}
            className="task-table__name-with-status"
            onClick={handleClick}
          >
            {/* Urgency indicator with tooltip */}
            <Tooltip title={urgencyConfig.label} placement="left">
              <span className={`task-table__urgency-indicator task-table__urgency-indicator--${urgencyConfig.cssClass}`} />
            </Tooltip>

            <div className="task-table__name-content">
              <div className="task-table__name">
                <span className="task-table__name-text">{text}</span>
                {record.recurring && (
                  <Tooltip title="Recurring task">
                    <SyncOutlined className="task-table__recurring-icon" style={{ color: link.blue, marginLeft: space[1.5] }} />
                  </Tooltip>
                )}
              </div>

              {/* Due date shown inline on mobile */}
              {isMobile && record.dueDate && (
                isOverdue ? (
                  <span className="task-table__overdue-pill">
                    <ExclamationCircleOutlined />
                    {daysLate}d late
                  </span>
                ) : (
                  <span className="task-table__due-inline">
                    {new Date(record.dueDate).toLocaleDateString('en-CA')}
                  </span>
                )
              )}
            </div>

            {/* Status badge - placed after name for proper hierarchy */}
            <span className={`task-table__inline-status task-table__inline-status--${statusClass}`}>
              {statusLabel}
            </span>
          </a>
        );
      },
    });

    // Due Date column (hide on mobile - shown inline with task name)
    if (!isMobile) {
      cols.push({
        title: 'Due',
        dataIndex: 'dueDate',
        key: 'dueDate',
        sorter: true,
        width: 150,
        className: 'task-table__cell--due',
        render: (dueDate, record) => {
          const { text, isOverdue, isToday, daysLate } = formatDueDate(dueDate, record.status);

          if (text === '—') {
            return <span className="task-table__no-date">—</span>;
          }

          if (isOverdue) {
            return (
              <Tooltip title={new Date(dueDate).toLocaleDateString()}>
                <span className="task-table__overdue-pill">
                  <ExclamationCircleOutlined />
                  {daysLate}d late
                </span>
              </Tooltip>
            );
          }

          if (isToday) {
            return (
              <span className="task-table__due-today">
                Today
              </span>
            );
          }

          return (
            <Tooltip title={new Date(dueDate).toLocaleDateString()}>
              <span className="task-table__due">
                {text}
              </span>
            </Tooltip>
          );
        },
      });
    }

    // Request Type column (hide on mobile and tablet)
    if (!isMobile && !isTablet) {
      cols.push({
        title: 'Type',
        dataIndex: 'requestType',
        key: 'requestType',
        width: 180,
        className: 'task-table__cell--type',
        render: (requestType, record) => (
          <span className="task-table__type">
            {requestType || 'N/A'}
            <br />
            <span style={{ color: text.placeholder }}>{record.requestSubType || ''}</span>
          </span>
        ),
      });
    }

    // Advertiser Type column (hide on mobile and tablet)
    if (!isMobile && !isTablet) {
      cols.push({
        title: 'Adv. Type',
        dataIndex: 'advertiserType',
        key: 'advertiserType',
        width: 120,
        className: 'task-table__cell--type',
        render: (_, record) => {
          const orgLevel =
            record.createdBy?.roles?.[0]?.org?.level;
          const formattedOrgLevel = orgLevel
            ? orgLevel
                .split('_')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
            : '—';
          return (
            <span className="task-table__type">
              {formattedOrgLevel}
            </span>
          );
        },
      });
    }

    // Advertiser column (hide on mobile and tablet)
    if (!isMobile && !isTablet) {
      cols.push({
        title: 'Advertiser',
        dataIndex: 'advertiser',
        key: 'advertiser',
        width: 210,
        className: 'task-table__cell--advertiser',
        render: (_, record) => {
          const orgName = record.createdBy?.roles?.[0]?.org?.name;
          return (
            <span className="task-table__advertiser" title={orgName}>
              {orgName || '—'}
            </span>
          );
        },
      });
    }

    // Created column (hide on mobile)
    if (!isMobile) {
      cols.push({
        title: 'Created',
        dataIndex: 'createdAt',
        key: 'createdAt',
        sorter: true,
        width: 120,
        className: 'task-table__cell--updated',
        render: (_, record) => (
          <Tooltip title={record.createdAt ? new Date(record.createdAt).toLocaleString() : ''}>
            <span className="task-table__updated">
              {record.createdAt ? new Date(record.createdAt).toLocaleDateString('en-CA') : '—'}
            </span>
          </Tooltip>
        ),
      });
    }

    // Action column (hide on mobile - use drawer instead)
    if (!isMobile) {
      cols.push({
        title: 'Action',
        key: 'action',
        width: 120,
        className: 'task-table__cell--action',
        render: (_, record) => (
          <Button
            type="link"
            size="small"
            className="task-table__action-link"
            onClick={() => {
              setSelectedUpdate(record);
              setIsModalVisible(true);
            }}
          >
            Update
          </Button>
        ),
      });
    }

    return cols;
  }, [isMobile, isTablet, handleTaskClick]);

  // Active filters for chips and badge count
  const activeFilters = useMemo(() => {
    const filters = [];

    if (searchParams.requestTypes) {
      filters.push({
        key: 'type',
        label: `Type: ${searchParams.requestTypes.split(',').length}`,
        category: 'type',
        onRemove: () => handleTaskTypeFilter([]),
      });
    }

    if (searchParams.requestSubTypes) {
      filters.push({
        key: 'subtype',
        label: `Subtype: ${searchParams.requestSubTypes.split(',').length}`,
        category: 'type',
        onRemove: () => handleTaskSubTypeFilter([]),
      });
    }

    if (searchParams.status) {
      filters.push({
        key: 'status',
        label: `Status: ${searchParams.status.split(',').length}`,
        category: 'status',
        onRemove: () => handleStatusFilter([]),
      });
    }

    if (searchParams.advertiserTypes) {
      filters.push({
        key: 'advType',
        label: `Adv. Type: ${searchParams.advertiserTypes.split(',').length}`,
        category: 'type',
        onRemove: () => handleAdvertiserTypeFilter([]),
      });
    }

    if (searchParams.advertiser) {
      filters.push({
        key: 'advertiser',
        label: `Advertiser: ${searchParams.advertiser.split(',').length}`,
        category: 'type',
        onRemove: () => handleAdvertiserFilter([]),
      });
    }

    if (searchParams.urgency) {
      filters.push({
        key: 'urgency',
        label: `Urgency: ${searchParams.urgency.split(',').length}`,
        category: 'urgency',
        onRemove: () => handleUrgencyFilter([]),
      });
    }

    return filters;
  }, [searchParams, handleTaskTypeFilter, handleTaskSubTypeFilter, handleStatusFilter, handleAdvertiserTypeFilter, handleAdvertiserFilter, handleUrgencyFilter]);

  const activeFilterCount = activeFilters.length;

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setSearchParams({
      requestTypes: '',
      requestSubTypes: '',
      advertiserTypes: '',
      advertiser: '',
      name: searchParams.name, // Keep search term
      urgency: '',
      status: '',
      showAllStatuses: false,
      showProjectTasks: false,
    });
    setUnassignedTaskPagination((prev) => ({ ...prev, current: 1 }));
  }, [searchParams.name, setSearchParams, setUnassignedTaskPagination]);

  // Render filter select helper (matches TaskBoardFilters pattern)
  const renderFilterSelect = ({
    placeholder,
    value,
    onChange,
    options,
    loading,
    onSearch,
    showSearch = false,
  }) => (
    <Select
      className="task-filter-select"
      dropdownClassName="task-filter-dropdown"
      mode="multiple"
      showSearch={showSearch}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      loading={loading}
      onSearch={onSearch}
      maxTagCount={0}
      maxTagPlaceholder={(omittedValues) => `${omittedValues.length} selected`}
      allowClear
      filterOption={(input, option) =>
        option?.children?.toLowerCase?.()?.indexOf?.(input.toLowerCase()) >= 0
      }
      dropdownMatchSelectWidth={false}
      dropdownStyle={{ minWidth: 200 }}
    >
      {options}
    </Select>
  );

  return (
    <>
      {/* Header Row: Search + Filter Button + Create Task */}
      <div className="unassigned-view__header">
        {/* Mobile: Search + Settings button row */}
        {isMobile ? (
          <div className="unassigned-view__mobile-search-row">
            <Input
              placeholder="Search tasks..."
              value={localSearchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="unassigned-view__search-mobile"
              allowClear
              prefix={<SearchOutlined style={{ color: text.muted }} />}
              onClear={() => {
                setLocalSearchValue('');
                debouncedSearchUpdate.cancel();
                setSearchParams({ ...searchParams, name: '' });
              }}
            />
            <button
              className={`task-board-header__icon-btn ${showFilters ? 'active' : ''} ${activeFilterCount > 0 ? 'has-badge' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
              aria-label="Filters"
            >
              <FilterOutlined />
              {activeFilterCount > 0 && (
                <span className="task-board-header__badge-dot" />
              )}
            </button>
          </div>
        ) : (
          <>
            <div className="unassigned-view__search-wrapper">
              <Input
                placeholder="Search tasks..."
                value={localSearchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="unassigned-view__search"
                allowClear
                prefix={<SearchOutlined style={{ color: text.muted }} />}
                onClear={() => {
                  setLocalSearchValue('');
                  debouncedSearchUpdate.cancel();
                  setSearchParams({ ...searchParams, name: '' });
                }}
              />
            </div>
            <div className="unassigned-view__actions">
              <Dropdown
                overlay={
                  <Menu className="task-board-utilities-menu">
                    <Menu.Item
                      key="filter"
                      icon={<FilterOutlined />}
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      {showFilters ? 'Hide Filters' : 'Show Filters'}
                      {activeFilterCount > 0 && (
                        <Badge count={activeFilterCount} size="small" style={{ marginLeft: space[2] }} />
                      )}
                    </Menu.Item>
                    {level === 'PLATFORM' && (
                      <Menu.Item
                        key="export"
                        icon={<DownloadOutlined />}
                        onClick={handleExportUnassigned}
                        disabled={isExporting}
                      >
                        {isExporting ? 'Exporting...' : 'Export Tasks'}
                      </Menu.Item>
                    )}
                  </Menu>
                }
                trigger={['click']}
                placement="bottomRight"
              >
                <Tooltip title="Options" placement="bottom">
                  <button
                    className={`task-board-header__icon-btn ${showFilters || activeFilterCount > 0 ? 'active' : ''}`}
                    aria-label="Options"
                  >
                    <SettingOutlined />
                    {activeFilterCount > 0 && (
                      <span className="task-board-header__badge-dot" />
                    )}
                  </button>
                </Tooltip>
              </Dropdown>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  history.push(`/tasks/form/`);
                }}
                className="unassigned-view__create-btn"
              >
                New
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Collapsible Filter Panel - Matches TaskBoardFilters design */}
      {showFilters && (
        <div className="task-filters__container">
          {/* Active Filter Chips */}
          {activeFilters.length > 0 && (
            <div className="task-filters__chips-bar">
              <div className="task-filters__chips">
                {activeFilters.slice(0, isMobile ? 2 : 5).map((filter) => (
                  <Tag
                    key={filter.key}
                    className={`task-filter-chip task-filter-chip--${filter.category}`}
                    closable
                    onClose={filter.onRemove}
                  >
                    {filter.label}
                  </Tag>
                ))}
                {activeFilters.length > (isMobile ? 2 : 5) && (
                  <Tag className="task-filter-chip task-filter-chip--more">
                    +{activeFilters.length - (isMobile ? 2 : 5)} more
                  </Tag>
                )}
              </div>
              <Button
                className="task-filters__clear-link"
                type="link"
                size="small"
                onClick={handleClearFilters}
              >
                Clear all
              </Button>
            </div>
          )}

          {/* Primary Filters Row - Status only for unassigned */}
          <div className="task-filters__primary">
            <div className="task-filters__primary-row">
              {/* Status */}
              <div className="task-filters__primary-item">
                {renderFilterSelect({
                  placeholder: 'Status',
                  value: searchParams.status ? searchParams.status.split(',') : [],
                  onChange: handleStatusFilter,
                  options: Object.keys(statusTypes).map((status) => (
                    <Option key={status} value={statusTypes[status]}>
                      {status}
                    </Option>
                  )),
                })}
              </div>

              {/* Urgency - Primary for unassigned since no assignee */}
              <div className="task-filters__primary-item">
                {renderFilterSelect({
                  placeholder: 'Urgency',
                  value: searchParams.urgency ? searchParams.urgency.split(',') : [],
                  onChange: handleUrgencyFilter,
                  options: Object.keys(urgencyTypes).map((type) => (
                    <Option key={type} value={urgencyTypes[type]}>
                      <Badge
                        color={urgencyColors[urgencyTypes[type]]}
                        style={{ marginRight: space[2] }}
                      />
                      {type}
                    </Option>
                  )),
                })}
              </div>

              {/* Close Button */}
              <button
                className="task-filters__close-btn"
                onClick={() => setShowFilters(false)}
                aria-label="Close filters"
              >
                <CloseOutlined />
              </button>
            </div>
          </div>

          {/* Secondary Filters - Grouped */}
          <div className="task-filters__secondary">
            {/* Type Group */}
            <div className="task-filters__group">
              <span className="task-filters__group-label">Type</span>
              <div className="task-filters__group-items">
                <div className="task-filters__group-item">
                  {renderFilterSelect({
                    placeholder: 'Request Type',
                    value: searchParams.requestTypes ? searchParams.requestTypes.split(',') : [],
                    onChange: handleTaskTypeFilter,
                    options: taskTypes.map((type) => (
                      <Option key={type} value={type}>
                        {type}
                      </Option>
                    )),
                  })}
                </div>
                <div className="task-filters__group-item">
                  {renderFilterSelect({
                    placeholder: 'Subtype',
                    value: searchParams.requestSubTypes ? searchParams.requestSubTypes.split(',') : [],
                    onChange: handleTaskSubTypeFilter,
                    options: Object.entries(subTypes).map(([mainType, subTypeArray]) => (
                      <OptGroup label={mainType} key={mainType}>
                        {subTypeArray.map((subType) => (
                          <Option key={`${mainType}-${subType}`} value={subType}>
                            {subType}
                          </Option>
                        ))}
                      </OptGroup>
                    )),
                  })}
                </div>
              </div>
            </div>

            {/* Organization Group */}
            {!isRestrictedTaskLevel(level) && (
              <div className="task-filters__group">
                <span className="task-filters__group-label">Organization</span>
                <div className="task-filters__group-items">
                  <div className="task-filters__group-item">
                    {renderFilterSelect({
                      placeholder: 'Adv. Type',
                      value: searchParams.advertiserTypes ? searchParams.advertiserTypes.split(',') : [],
                      onChange: handleAdvertiserTypeFilter,
                      options: Object.keys(advertiserTypes).map((type) => (
                        <Option key={type} value={advertiserTypes[type]}>
                          {type}
                        </Option>
                      )),
                    })}
                  </div>
                  <div className="task-filters__group-item">
                    {renderFilterSelect({
                      placeholder: 'Advertiser',
                      value: searchParams.advertiser ? searchParams.advertiser.split(',') : [],
                      onChange: handleAdvertiserFilter,
                      loading: basicOrgsDetailsLoading,
                      onSearch: fetchAdvertiserOrgs,
                      showSearch: true,
                      options: basicOrgsDetails.map((org) => (
                        <Option key={org.id} value={org.name}>
                          {org.name}
                        </Option>
                      )),
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Options Group */}
            <div className="task-filters__group task-filters__group--options">
              <div className="task-filters__options-row">
                <Checkbox
                  className="task-filters__checkbox"
                  checked={searchParams.showAllStatuses === true || searchParams.showAllStatuses === 'true'}
                  onChange={(e) => {
                    setSearchParams({ ...searchParams, showAllStatuses: e.target.checked });
                    setUnassignedTaskPagination((prev) => ({ ...prev, current: 1 }));
                  }}
                >
                  Show all statuses
                </Checkbox>
                <Tooltip
                  title={
                    <div style={{ maxWidth: 280 }}>
                      <div style={{ marginBottom: space[2] }}>
                        <strong>Unchecked:</strong> Shows only tasks with default statuses
                      </div>
                      <div style={{ marginBottom: space[2], paddingLeft: space[3], color: text.hint }}>
                        • Not Started <br /> • In Progress <br /> • Ready for Review
                      </div>
                      <div style={{ marginBottom: space[2] }}>
                        <strong>Checked:</strong> Shows tasks with all statuses
                      </div>
                    </div>
                  }
                  placement="right"
                >
                  <QuestionCircleOutlined
                    style={{
                      color: activityIcon.gray,
                      fontSize: icon.sm,
                      cursor: 'help',
                      marginLeft: space[1],
                    }}
                  />
                </Tooltip>

                {level === 'PLATFORM' && (
                  <div className="task-filters__project-toggle">
                    <span>Projects:</span>
                    <Checkbox
                      checked={projectFilter === 'projectOnly'}
                      onChange={(e) => {
                        setProjectFilter(e.target.checked ? 'projectOnly' : 'noProject');
                        setUnassignedTaskPagination((prev) => ({ ...prev, current: 1 }));
                      }}
                    >
                      Only
                    </Checkbox>
                    <Checkbox
                      checked={projectFilter === 'noProject'}
                      onChange={(e) => {
                        setProjectFilter(e.target.checked ? 'noProject' : 'projectOnly');
                        setUnassignedTaskPagination((prev) => ({ ...prev, current: 1 }));
                      }}
                    >
                      Exclude
                    </Checkbox>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile: Active Filters Summary */}
      {isMobile && activeFilters.length > 0 && !showFilters && (
        <div className="task-filters__mobile-summary">
          <span>{activeFilters.length} filter{activeFilters.length > 1 ? 's' : ''} active</span>
          <Button type="link" size="small" onClick={handleClearFilters}>
            Clear all
          </Button>
        </div>
      )}

      <div className="task-board-table-modern">
        {!isMobile && (
          <Table
            columns={columns}
            dataSource={displayTasks}
            loading={tasksLoading && accumulatedTasks.length === 0}
            pagination={false}
            onChange={handleTableChange}
            scroll={{ x: isTablet ? 1000 : 1400 }}
            className="task-table"
            rowKey={(record) => record.id}
            rowClassName={(record) => getRowClassName(record)}
            size="middle"
          />
        )}

        {isMobile && (
          <TaskMobileCards
            tasks={displayTasks}
            loading={tasksLoading}
            onTaskClick={handleTaskClick}
            level={level}
          />
        )}

        {/* Desktop: Enhanced Three-Column Pagination */}
        {!isMobile && (
          <div className="task-pagination-enhanced">
            <div className="task-pagination-enhanced__left">
              <span className="task-pagination-enhanced__summary">
                {unassignedTaskPagination.total > 0 ? (
                  <>
                    Showing <strong>{(unassignedTaskPagination.current - 1) * unassignedTaskPagination.pageSize + 1}</strong>
                    {' - '}
                    <strong>{Math.min(unassignedTaskPagination.current * unassignedTaskPagination.pageSize, unassignedTaskPagination.total)}</strong>
                    {' of '}
                    <strong>{unassignedTaskPagination.total}</strong> tasks
                  </>
                ) : (
                  'No tasks found'
                )}
              </span>
            </div>

            <div className="task-pagination-enhanced__center">
              <Pagination
                current={unassignedTaskPagination.current}
                pageSize={unassignedTaskPagination.pageSize}
                total={unassignedTaskPagination.total}
                showSizeChanger={false}
                hideOnSinglePage={false}
                onChange={(page) => handlePaginationChange({ page, pageSize: unassignedTaskPagination.pageSize })}
                itemRender={itemRender}
                className="task-pagination-enhanced__nav"
              />
            </div>

            <div className="task-pagination-enhanced__right">
              <span className="task-pagination-enhanced__size-label">Show:</span>
              <div className="task-pagination-enhanced__size-options">
                {[10, 25, 50, 100].map((size) => (
                  <button
                    key={size}
                    className={`task-pagination-enhanced__size-btn ${unassignedTaskPagination.pageSize === size ? 'task-pagination-enhanced__size-btn--active' : ''}`}
                    onClick={() => handlePaginationSizeChange(1, size)}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Mobile: Infinite Scroll */}
        {isMobile && unassignedTaskPagination.total > 0 && (
          <div className="task-mobile-pagination">
            {/* Progress indicator */}
            <div className="task-mobile-pagination__progress">
              <div className="task-mobile-pagination__progress-bar">
                <div
                  className="task-mobile-pagination__progress-fill"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className="task-mobile-pagination__progress-text">
                {accumulatedTasks.length} of {unassignedTaskPagination.total}
              </span>
            </div>

            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} className="task-mobile-pagination__sentinel">
              {tasksLoading && hasMorePages && (
                <div className="task-mobile-pagination__loading">
                  <Spin indicator={<LoadingOutlined spin />} size="small" />
                  <span>Loading more...</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <TaskUpdateModal
        visible={isModalVisible}
        onCancel={handleCancel}
        onUpdate={handleUpdate}
        selected={selectedUpdate}
        loading={updateTaskLoading}
      />

      {/* Floating Action Button for Mobile */}
      {isMobile && (
        <Button
          type="primary"
          shape="circle"
          icon={<PlusOutlined />}
          className="task-board__fab"
          onClick={() => history.push('/tasks/form/')}
          aria-label="Create new task"
        />
      )}

      <TaskDrawerFull
        visible={drawerVisible}
        onClose={handleDrawerClose}
        taskId={selectedTaskId}
        onTaskUpdate={handleTaskUpdate}
      />

      <BackToTop />
    </>
  );
};

export default withRouter(UnassignedTasksView);
